import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ConsortiumCategory } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CategoryHero } from '@/components/landing/category-hero'
import { CategoryUsesSection } from '@/components/landing/category-uses-section'
import { SimulatorSection } from '@/components/landing/simulator-section'
import { FaqSection } from '@/components/landing/faq-section'
import { LeadFormSection } from '@/components/landing/lead-form-section'
import { OtherCategoriesSection } from '@/components/landing/other-categories-section'
import { useProposalSelection } from '@/hooks/use-proposal-selection'
import { consortiumCategories, getConsortiumCategory } from '@/constants/consorcios'

interface CategoryPageProps {
    category: ConsortiumCategory
}

const CategoryContent: FC<CategoryPageProps> = ({ category }): ReactNode => {
    const { selection, requestProposal, clearSelection } = useProposalSelection()
    const lowerTitle = category.title.toLowerCase()

    return (
        <>
            <CategoryHero category={category} />
            <CategoryUsesSection category={category} />

            <SimulatorSection
                initialSlug={category.slug}
                title={`Simule seu ${lowerTitle}`}
                description='Ajuste o valor do crédito e o prazo para ver a parcela na hora. Se quiser, compare com os outros tipos de consórcio.'
                onRequestProposal={requestProposal}
            />

            <FaqSection
                items={category.faq}
                title={`Dúvidas sobre ${lowerTitle}`}
                description='O que mais perguntam antes de entrar em um grupo deste tipo.'
            />

            <LeadFormSection
                defaultSlug={category.slug}
                title={`Receba uma proposta de ${lowerTitle}`}
                selection={selection}
                onClearSelection={clearSelection}
            />

            <OtherCategoriesSection current={category.slug} />
        </>
    )
}

const CategoryPage: FC<CategoryPageProps> = ({ category }): ReactNode => {
    return (
        <Layout ctaHref='#contato'>
            <Seo title={category.title} description={category.seoDescription} />

            <CategoryContent key={category.slug} category={category} />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: consortiumCategories.map(category => ({ params: { slug: category.slug } })),
        fallback: false
    }
}

export const getStaticProps: GetStaticProps<CategoryPageProps, { slug: string }> = async ({ params }) => {
    const category = params ? getConsortiumCategory(params.slug) : undefined

    if (!category) return { notFound: true }

    return {
        props: {
            category
        }
    }
}

export default CategoryPage
